import { defineModule } from "../define.ts";
import type { ModuleEventDef } from "@vavr/core";

/**
 * School buses (docs/spec/transport.md): routes, stops, riders and vehicles owned by an organisation
 * (a school, or later a bus company under contract), and every child getting on and off recorded as
 * evidence in the child's school chain. An add-on: off until an operator switches it on.
 * Dedicated dispatcher and driver roles come with bus companies (slice 5); until then organisation
 * administrators manage transport and staff run the bus.
 */

const ID = /^[a-z]{2,4}_[0-9a-z]{8,32}$/;
type Pre = Readonly<Record<string, unknown>>;
const dataOf = (pre: Pre) => (pre.data ?? {}) as Record<string, unknown>;
/** Every bus event names its run and stop. */
function stopRules(pre: Pre): string[] {
  const d = dataOf(pre);
  const errs: string[] = [];
  if (typeof d.run_id !== "string" || !ID.test(d.run_id)) errs.push("data.run_id invalid");
  if (typeof d.stop_id !== "string" || !ID.test(d.stop_id)) errs.push("data.stop_id invalid");
  return errs;
}

const events: ModuleEventDef[] = [
  {
    type: "transport:board", label: "Got on the bus", child: "required", staff: "required", devices: ["phone"],
    validate: (pre) => [...stopRules(pre), ...(pre.adult_id !== null ? ["transport:board names no adult"] : [])],
    // On the bus the school has the child, as in a room.
    presence: () => ({ state: "in", custodian: "bus" }),
  },
  {
    type: "transport:alight", label: "Got off the bus", child: "required", staff: "required", devices: ["phone"],
    validate: (pre) => {
      const d = dataOf(pre);
      const errs = stopRules(pre);
      if (d.handover === true && pre.adult_id === null) errs.push("a handover at a stop names the adult");
      if (d.handover !== true && pre.adult_id !== null) errs.push("transport:alight names an adult only on a handover");
      if (d.handover === true && d.at_school === true) errs.push("at school the child goes to staff, not an adult");
      return errs;
    },
    // Met by an adult at the stop: checked like a pickup (authorised, restrictions).
    handsOver: (pre) => (pre.data as Record<string, unknown> | null)?.handover === true,
    presence: (pre) => ((pre.data as Record<string, unknown> | null)?.at_school === true ? { state: "in", custodian: "door" } : { state: "out", custodian: null }),
  },
  {
    type: "transport:absent", label: "Not at the bus stop", child: "required", staff: "required", devices: ["phone"],
    validate: stopRules,
  },
  {
    type: "transport:held", label: "Kept on the bus: no approved adult at the stop", child: "required", staff: "required", devices: ["phone"],
    validate: stopRules,
    presence: () => ({ state: "in", custodian: "bus" }),
  },
];

export const transport = defineModule({
  id: "transport",
  name: "Transport",
  version: "0.3.0",
  description: "School buses: routes, stops and riders, and every child getting on and off the bus recorded as evidence.",
  entitlement: "addon:transport",
  // Bus companies' staff (slice 5): a dispatcher plans routes for the whole company; a driver runs the bus.
  roles: [
    // Both are on the radio (at a bus company's depot, the company's own channel) and in its staff messages.
    { id: "transport:dispatcher", label: "Transport dispatcher", permissions: ["transport:manage", "transport:ride", "radio:use", "messages:staff", "lostfound:use"] },
    { id: "transport:driver", label: "Bus driver or attendant", permissions: ["transport:ride", "radio:use", "messages:staff", "lostfound:use"] },
  ],
  grants: {
    "transport:manage": ["org_admin"],
    // Only bus crews (the roles above) and organisation administrators run a bus: teachers and door
    // staff do not (decided 2026-09-30). A school that puts a teacher on a bus gives them the driver
    // role as well.
    "transport:ride": ["org_admin"],
  },
  events,
});
